import { useCallback, useMemo } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, Collapse, IconButton } from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { useState } from 'react';
import { gwtStyles } from '../../config/theme';
import { useUIStore } from '../../stores/uiStore';
import type { AggregationBucket } from '../../api/types';

/**
 * GWT-matching aggregation panel — mirrors AggregationPanel.java.
 *
 * A tree of facet categories with checkbox items.
 * Pre-populated with default facet categories from StudyField.aggrKeys.
 * Actual bucket counts come from the server when available.
 * Clicking a checkbox sets a filter in the UI store.
 */

interface FacetCategory {
  id: string;
  label: string;
  items: { id: string; label: string; count: number }[];
}

/** Default facet categories — populated even when server aggregations unavailable */
const DEFAULT_FACETS: FacetCategory[] = [
  {
    id: 'priority',
    label: 'Priority',
    items: [
      { id: 'STROKE', label: 'STROKE', count: 0 },
      { id: 'STAT', label: 'STAT', count: 0 },
      { id: 'ASAP', label: 'ASAP', count: 0 },
      { id: 'ROUTINE', label: 'ROUTINE', count: 0 },
    ],
  },
  {
    id: 'status',
    label: 'Status',
    items: [
      { id: 'ORDERED', label: 'Ordered', count: 0 },
      { id: 'SCHEDULED', label: 'Scheduled', count: 0 },
      { id: 'INPROGRESS', label: 'In Progress', count: 0 },
      { id: 'NEWSTUDY', label: 'New Study', count: 0 },
      { id: 'ASSIGNED', label: 'Assigned', count: 0 },
      { id: 'DELIVERED', label: 'Delivered', count: 0 },
      { id: 'READ', label: 'Read', count: 0 },
      { id: 'DICTATED', label: 'Dictated', count: 0 },
      { id: 'TRANSCRIBED', label: 'Transcribed', count: 0 },
      { id: 'REPORTED', label: 'Reported', count: 0 },
      { id: 'AMENDED', label: 'Amended', count: 0 },
    ],
  },
  {
    id: 'modality',
    label: 'Modality',
    items: [],
  },
  {
    id: 'site',
    label: 'Site',
    items: [],
  },
  {
    id: 'radiologist',
    label: 'Radiologist',
    items: [],
  },
  {
    id: 'bodypart',
    label: 'Body Part',
    items: [],
  },
];

interface AggregationPanelProps {
  /** Server-side aggregation buckets (when available) */
  aggregations?: Record<string, AggregationBucket[]>;
}

/**
 * Merge server aggregations into the default facet categories.
 * If the server provides counts for a category, update the items;
 * if it provides new items, add them.
 */
function mergeFacets(
  defaults: FacetCategory[],
  serverAggregations?: Record<string, AggregationBucket[]>,
): FacetCategory[] {
  if (!serverAggregations) return defaults;

  return defaults.map((cat) => {
    const serverBuckets = serverAggregations[cat.id];
    if (!serverBuckets || serverBuckets.length === 0) return cat;

    // Build a map from existing default items
    const existingMap = new Map(cat.items.map((item) => [item.id, item]));

    // Update counts from server data, add new items
    for (const bucket of serverBuckets) {
      const existing = existingMap.get(bucket.key);
      if (existing) {
        existing.count = bucket.count;
      } else {
        existingMap.set(bucket.key, {
          id: bucket.key,
          label: bucket.label ?? bucket.key,
          count: bucket.count,
        });
      }
    }

    return { ...cat, items: Array.from(existingMap.values()) };
  });
}

export const AggregationPanel = ({ aggregations }: AggregationPanelProps) => {
  const facets = useMemo(() => mergeFacets(DEFAULT_FACETS, aggregations), [aggregations]);

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        backgroundColor: gwtStyles.tabContent,
        borderLeft: '1px solid',
        borderColor: 'divider',
        p: 0.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontSize: 11, fontWeight: 600, color: gwtStyles.tabTitle, px: 0.5, pb: 0.5, display: 'block' }}
      >
        Filters
      </Typography>
      {facets.map((category) => (
        <FacetCategoryNode key={category.id} category={category} />
      ))}
    </Box>
  );
};

const FacetCategoryNode = ({ category }: { category: FacetCategory }) => {
  const [expanded, setExpanded] = useState(false);
  const activeFilters = useUIStore((s) => s.activeFilters);
  const setFilter = useUIStore((s) => s.setFilter);
  const hasItems = category.items.length > 0;

  const selectedValues = activeFilters[category.id] ?? [];

  const handleToggle = useCallback(
    (itemId: string) => {
      const current = activeFilters[category.id] ?? [];
      const next = current.includes(itemId)
        ? current.filter((v) => v !== itemId)
        : [...current, itemId];
      setFilter(category.id, next);
    },
    [activeFilters, category.id, setFilter],
  );

  return (
    <Box sx={{ mb: 0.25 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: hasItems ? 'pointer' : 'default',
          '&:hover': hasItems ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
          borderRadius: 0.5,
          px: 0.25,
        }}
        onClick={() => hasItems && setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ width: 20, height: 20, p: 0 }} disabled={!hasItems}>
          {expanded ? <ExpandMore sx={{ fontSize: 14 }} /> : <ChevronRight sx={{ fontSize: 14 }} />}
        </IconButton>
        <Typography
          variant="caption"
          sx={{ fontSize: 11, fontWeight: 600, color: 'text.primary' }}
        >
          {category.label}
          {selectedValues.length > 0 && ` (${selectedValues.length})`}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ pl: 2.5 }}>
          {category.items.map((item) => (
            <FormControlLabel
              key={item.id}
              control={
                <Checkbox
                  size="small"
                  sx={{ p: 0.25 }}
                  checked={selectedValues.includes(item.id)}
                  onChange={() => handleToggle(item.id)}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontSize: 11 }}>
                  {item.label}
                  {item.count > 0 && ` (${item.count})`}
                </Typography>
              }
              sx={{ display: 'flex', m: 0, height: 22 }}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
